import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  Building2, ShieldCheck, ScrollText, LayoutDashboard, Users, UserPlus,
  UserCheck, Link2, Database, UserCog, LogOut, Briefcase, GitPullRequest, FileSpreadsheet, Calculator, ClipboardCheck, CalendarDays, CalendarHeart, MapPinned, Menu,
} from "lucide-react";

const SUPER_NAV = [
  { to: "/admin/organisations", label: "Organisations", icon: Building2 },
  { to: "/admin/super-admins", label: "Super Admins", icon: ShieldCheck },
  { to: "/admin/change-requests", label: "Change Requests", icon: GitPullRequest },
  { to: "/admin/payroll-review", label: "Payroll Review", icon: ClipboardCheck },
  { to: "/admin/final-rosters", label: "Final Rosters", icon: CalendarDays },
  { to: "/admin/export-templates", label: "Export Templates", icon: FileSpreadsheet },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
];

const ORG_NAV = [
  { to: "/org/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/org/employees", label: "Employees", icon: Users },
  { to: "/org/invite", label: "Invite", icon: UserPlus },
  { to: "/org/payroll", label: "Payroll", icon: Calculator },
  { to: "/org/roster", label: "Roster", icon: CalendarDays },
  { to: "/org/change-requests", label: "Change Requests", icon: GitPullRequest },
  { to: "/org/export-templates", label: "Export Templates", icon: FileSpreadsheet },
  { to: "/org/pending-signups", label: "Pending Signups", icon: UserCheck },
  { to: "/org/join-link", label: "Join Link", icon: Link2 },
  { to: "/org/masters", label: "Masters", icon: Database },
  { to: "/org/holidays", label: "Holidays", icon: CalendarHeart },
  { to: "/org/org-admins", label: "Org Admins", icon: UserCog },
  { to: "/org/center-managers", label: "Center Managers", icon: MapPinned },
];

const MANAGER_NAV = [
  { to: "/org/roster", label: "Roster", icon: CalendarDays },
  { to: "/org/employees", label: "Employees", icon: Users },
  { to: "/org/holidays", label: "Holidays", icon: CalendarHeart },
];

const Brand = () => (
  <div className="flex h-16 shrink-0 items-center gap-2 border-b border-slate-100 px-6">
    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-white"><Briefcase className="h-4 w-4" /></div>
    <div className="leading-tight">
      <p className="text-sm font-bold text-slate-900">Infiblu</p>
      <p className="text-[11px] text-slate-400">Workforce Portal</p>
    </div>
  </div>
);

export default function Layout() {
  const { user, logout, actingOrgId, setActingOrg, isManagerOnly } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const isSuper = user?.roles?.includes("super_admin");
  const isOrgAdmin = user?.roles?.includes("org_admin") || isManagerOnly;
  const acting = Boolean(actingOrgId);

  // Ensure a plain org admin / center manager always has an acting org set so X-Org-Id is sent.
  useEffect(() => {
    if (!isSuper && isOrgAdmin && (!actingOrgId || !user?.orgs?.some((o) => o.id === actingOrgId)) && user?.orgs?.length) {
      setActingOrg(user.orgs[0].id);
    }
  }, [isSuper, isOrgAdmin, actingOrgId, user, setActingOrg]);

  const showOrgNav = acting || (!isSuper && isOrgAdmin);
  const nav = isManagerOnly ? MANAGER_NAV : showOrgNav ? ORG_NAV : SUPER_NAV;
  const currentOrg = user?.orgs?.find((o) => o.id === actingOrgId);

  const onSwitch = (val) => {
    if (val === "__admin__") {
      setActingOrg("");
      navigate("/admin/organisations");
    } else {
      setActingOrg(val);
      navigate(isManagerOnly ? "/org/roster" : "/org/dashboard");
    }
  };

  const showSwitch = isSuper || (isOrgAdmin && user?.orgs?.length > 1);
  const doLogout = () => { logout(); navigate("/login"); };
  const panel = (prefix, onPick) => (
    <>
      {showSwitch && (
        <div className="border-b border-slate-100 px-4 py-3">
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">{isSuper ? "Acting as" : "Organisation"}</p>
          <Select value={actingOrgId || "__admin__"} onValueChange={(v) => { onSwitch(v); onPick?.(); }}>
            <SelectTrigger data-testid={`${prefix}org-switcher`} className="h-10 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {isSuper && <SelectItem value="__admin__">Infiblu Admin</SelectItem>}
              {user?.orgs?.map((o) => <SelectItem key={o.id} value={o.id} data-testid={`${prefix}org-option-${o.id}`}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        {nav.map((item) => (
          <NavLink key={item.to} to={item.to} onClick={onPick} data-testid={`${prefix}nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-indigo-50 text-indigo-700" : "text-slate-600 hover:bg-slate-100"}`}>
            <item.icon className="h-4 w-4" />{item.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-slate-100 p-4">
        <p className="truncate text-sm font-medium text-slate-700">{user?.name}</p>
        <p className="mb-3 truncate text-xs text-slate-400">{user?.email}</p>
        <Button variant="outline" size="sm" className="w-full" data-testid={`${prefix}logout-btn`} onClick={doLogout}>
          <LogOut className="mr-2 h-3.5 w-3.5" /> Log out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-slate-200 bg-white lg:flex">
        <Brand />
        {panel("")}
      </aside>

      <div className="lg:pl-64">
        {acting && isSuper && (
          <div className="flex items-center justify-between gap-2 bg-indigo-600 px-4 py-2 text-sm text-white sm:px-6" data-testid="acting-banner">
            <span>Acting as <strong>{currentOrg?.name}</strong></span>
            <button className="-my-1 -mr-2 rounded px-2 py-1 underline" data-testid="exit-acting" onClick={() => { setActingOrg(""); navigate("/admin/organisations"); }}>
              Exit
            </button>
          </div>
        )}
        <div className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white px-3 py-2 lg:hidden">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="flex w-72 flex-col p-0" data-testid="mobile-menu">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <Brand />
              {panel("m-", () => setMenuOpen(false))}
            </SheetContent>
          </Sheet>
          <span className="truncate px-2 text-sm font-bold text-slate-900" data-testid="mobile-org-name">{currentOrg?.name || "Infiblu"}</span>
          <Button variant="ghost" size="icon" aria-label="Log out" onClick={doLogout} data-testid="mobile-logout-btn"><LogOut className="h-4 w-4" /></Button>
        </div>
        <main className="mx-auto max-w-6xl px-3 py-5 sm:px-6 sm:py-8 lg:px-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
